import React, { useEffect, useState } from 'react';
import { isNativeApp } from '../utils/platform';
import { ensureFriendLink } from '../utils/homeownerApi';
import { shareLink, shareWasCancelled } from '../utils/nativeShare';

export default function InviteFriendPane({ colors, invitePreview }) {
  const preview = invitePreview || {};
  const [link, setLink] = useState(preview.url || '');
  const [sheet, setSheet] = useState(!!preview.sheet);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (link) return undefined;
    let cancel = false;
    ensureFriendLink().then((data) => {
      if (!cancel) setLink((data && data.url) || '');
    }).catch((loadError) => {
      if (!cancel) setError((loadError && loadError.message) || 'Could not create the link.');
    });
    return () => { cancel = true; };
  }, [link]);

  const share = async () => {
    if (!link) return;
    if (isNativeApp()) {
      try {
        await shareLink(link, 'Join me on Pacarion');
      } catch (shareError) {
        if (!shareWasCancelled(shareError)) setError('Could not share the link.');
      }
      return;
    }
    setSheet(true);
  };

  const copy = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(link);
        setNote('Copied.');
      }
    } catch (copyError) {
      setNote('Could not copy the link.');
    }
  };

  const shareFromSheet = async () => {
    try {
      await shareLink(link, 'Join me on Pacarion');
      setSheet(false);
    } catch (shareError) {
      if (!shareWasCancelled(shareError)) setNote('Could not share the link.');
    }
  };

  return (
    <div>
      <h2 style={{ fontWeight: 300, fontSize: '1.5rem', marginTop: 0, color: colors.text }}>Invite a friend</h2>
      <p style={{ color: colors.textSecondary, lineHeight: 1.5, marginTop: 0 }}>
        Share a link. Your friend can use your sponsor, or start their own account. You won't see their home.
      </p>
      {error ? <p role="alert">{error}</p> : null}
      <button
        type="button"
        data-testid="invite-friend-share"
        onClick={share}
        disabled={!link}
        style={{
          width: '100%',
          minHeight: '48px',
          border: 'none',
          borderRadius: '12px',
          background: '#59c4c4',
          color: '#083838',
          fontWeight: 700,
          fontSize: '1rem',
          cursor: link ? 'pointer' : 'default',
          opacity: link ? 1 : 0.6,
        }}
      >
        Share
      </button>
      {sheet ? (
        <div
          data-testid="invite-friend-sheet"
          role="dialog"
          aria-label="Share invite"
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 40,
            background: colors.surface,
            color: colors.text,
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
            boxShadow: '0 -8px 30px rgba(0,0,0,0.18)',
            padding: '1rem 1.1rem',
            paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))',
          }}
        >
          <p style={{ margin: '0 0 0.75rem', wordBreak: 'break-all', fontSize: '0.95rem' }}>{link}</p>
          {note ? <p style={{ margin: '0 0 0.5rem', color: colors.textSecondary }}>{note}</p> : null}
          <button type="button" onClick={shareFromSheet} style={sheetButton(colors, true)}>Share</button>
          <button type="button" onClick={copy} style={sheetButton(colors, false)}>Copy</button>
          <button type="button" onClick={() => setSheet(false)} style={sheetButton(colors, false)}>Cancel</button>
        </div>
      ) : null}
    </div>
  );
}

function sheetButton(colors, filled) {
  return {
    width: '100%',
    minHeight: '48px',
    marginTop: '0.45rem',
    borderRadius: '12px',
    border: filled ? 'none' : `1px solid ${colors.border}`,
    background: filled ? '#59c4c4' : 'transparent',
    color: filled ? '#083838' : colors.text,
    fontWeight: 700,
    fontSize: '1rem',
    cursor: 'pointer',
  };
}
